import _ from "lodash";
import { call, put, takeLatest, takeEvery } from "redux-saga/effects";
import { uploadFile } from "shared-utility/store/utils/fileHelper";
import {
  defaultReturn
  , makePayload
  , makeWritePayload
  , generateDefaultActions
  , requestSaga
  , callbackSaga
  , subKeySaga
  , fillDictionary
  , handleCallback
  , multipleObjectReturn
} from "shared-utility/store/utils/helpers";
import { authorizedRequest, downloadXLSFile, downloadFile } from "utils/api";

import { ToastSuccess, ToastError, ToastInfo } from "../../components/Global/Toasts/Toasty";

// ACTIONS

export const LOAN_DETAIL_DELETE_LOAN = generateDefaultActions("LOAN_DETAIL_DELETE_LOAN");
export const deleteLoan = payload => makePayload(LOAN_DETAIL_DELETE_LOAN.value, payload);
function* deleteLoanSaga(payload) {
  const callback = handleCallback(payload);
  const url = `/loans/deleteLoan/${payload.loanId}`;
  yield requestSaga(LOAN_DETAIL_DELETE_LOAN, url, "DELETE");
  yield callback();
}

const LOAN_DETAIL_GET_LOAN = generateDefaultActions("LOAN_DETAIL_GET_LOAN");
export const loanDetailGetLoan = payload => makePayload(LOAN_DETAIL_GET_LOAN.value, payload);
function* loanDetailGetLoanSaga(payload) {
  const url = `/loans/getLoanDetails/${payload.loanId}`;
  yield requestSaga(LOAN_DETAIL_GET_LOAN, url, "GET");
}

const LOAN_DETAIL_GET_DEAL_METRICS = generateDefaultActions("LOAN_DETAIL_GET_DEAL_METRICS");
export const loanDetailGetDealMetrics = payload => makePayload(LOAN_DETAIL_GET_DEAL_METRICS.value, payload);
function* loanDetailGetDealMetricsSaga(payload) {
  const { loanId, dealMetrics } = payload;
  const url = `/loans/${loanId}/dealMetrics`;

  yield requestSaga(LOAN_DETAIL_GET_DEAL_METRICS, url, "POST", { dealMetrics });
}

export const LOAN_DETAIL_UPDATE_LOAN_DETAILS = generateDefaultActions("LOAN_DETAIL_UPDATE_LOAN_DETAILS");
export const updateLoanDetails = payload => makePayload(LOAN_DETAIL_UPDATE_LOAN_DETAILS.value, payload);
function* updateLoanDetailsSaga(payload) {
  const url = `/loans/updateLoanDetails/${payload.id}`;
  yield* requestSaga(LOAN_DETAIL_UPDATE_LOAN_DETAILS, url, "POST", payload.loanDetails);
  if (payload.callback) {
    payload.callback();
  }
}

export const LOAN_DETAIL_UPDATE_FIELD_SUMMARY = generateDefaultActions("LOAN_DETAIL_UPDATE_FIELD_SUMMARY");
export const updateFieldSummary = payload => makePayload(LOAN_DETAIL_UPDATE_FIELD_SUMMARY.value, payload);
function* updateFieldSummarySaga(payload) {
  const callback = handleCallback(payload);
  const url = "/loans/updateFieldSummary";
  yield* requestSaga(LOAN_DETAIL_UPDATE_LOAN_DETAILS, url, "POST", payload);
  yield callback();
}

export const LOAN_DETAIL_UPDATE_RECOURSE_SCHEDULE = generateDefaultActions("LOAN_DETAIL_UPDATE_RECOURSE_SCHEDULE");
export const saveRecourseScheduleChange = payload => makePayload(LOAN_DETAIL_UPDATE_RECOURSE_SCHEDULE.value, payload);
function* saveRecourseScheduleChangeSaga(payload) {
  const callback = handleCallback(payload);
  const url = "/loans/saveRecourseScheduleChange";
  yield* requestSaga(LOAN_DETAIL_UPDATE_RECOURSE_SCHEDULE, url, "POST", payload);
  yield callback();
}
// START NOTES
export const LOAN_DETAIL_GET_NOTES = generateDefaultActions("LOAN_DETAIL_GET_NOTES");
export const loanGetNotes = payload => makePayload(LOAN_DETAIL_GET_NOTES.value, payload);
function* loanGetNotesSaga(payload) {
  const url = `/loans/notes/${payload.loanId}`;
  const requestSaga = subKeySaga(payload.subKey);
  yield requestSaga(LOAN_DETAIL_GET_NOTES, url, "GET");
}

export const LOAN_DETAIL_PUT_NOTE = generateDefaultActions("LOAN_DETAIL_PUT_NOTE");
export const loanPutNote = payload => makePayload(LOAN_DETAIL_PUT_NOTE.value, payload);
function* loanPutNoteSaga(payload) {
  const url = `/notes/${payload.note.id}`;
  yield requestSaga(LOAN_DETAIL_PUT_NOTE, url, "PUT", payload);
  yield put(loanGetNotes({ loanId: payload.loanId }));
}

export const LOAN_DETAIL_POST_NOTE = generateDefaultActions("LOAN_DETAIL_POST_NOTE");
export const loanPostNote = payload => makePayload(LOAN_DETAIL_POST_NOTE.value, payload);
function* loanPostNoteSaga(payload) {
  const url = "/notes";
  const note = {
    model: "loan",
    modelId: payload.loanId,
    note: payload.note,
    pinned: false
  };
  yield requestSaga(LOAN_DETAIL_POST_NOTE, url, "POST", { note });
  yield put(loanGetNotes({ loanId: payload.loanId }));
}

export const LOAN_DETAIL_DELETE_NOTE = generateDefaultActions("LOAN_DETAIL_DELETE_NOTE");
export const loanDeleteNote = payload => makePayload(LOAN_DETAIL_DELETE_NOTE.value, payload);
function* loanDeleteNoteSaga(payload) {
  const url = `/notes/${payload.noteId}`;
  yield requestSaga(LOAN_DETAIL_DELETE_NOTE, url, "DELETE");
  yield put(loanGetNotes({ loanId: payload.loanId }));
}
// END NOTES

// START REFERENCES
export const LOAN_DETAIL_GET_REFERENCE_FIELDS = generateDefaultActions("LOAN_DETAIL_GET_REFERENCE_FIELDS");
export const loanGetReferenceFields = () => makePayload(LOAN_DETAIL_GET_REFERENCE_FIELDS.value);
function* loanGetReferenceFieldsSaga() {
  yield requestSaga(LOAN_DETAIL_GET_REFERENCE_FIELDS, "/referenceFields", "GET");
}

export const LOAN_DETAIL_ADD_REFERENCE = generateDefaultActions("LOAN_DETAIL_ADD_REFERENCE");
export const loanAddReference = payload => ({ type: LOAN_DETAIL_ADD_REFERENCE.value, reference: payload });
function* loanAddReferenceSaga(payload) {
  try {
    const reference = {
      referenceFieldId: payload.reference.referenceField.id,
      modelId: payload.reference.modelId,
      fileLinkId: payload.reference.fileLink.id,
      title: payload.reference.title,
      text: payload.reference.text,
      pdfAnnotation: payload.reference.pdfAnnotation,
      sortOrder: payload.reference.sortOrder
    };
    const response = yield call(authorizedRequest, "/reference", "POST", reference);
    yield put({ type: LOAN_DETAIL_ADD_REFERENCE.success, payload: response, originalPayload: payload.reference });
    payload.reference.id = response.id;

    return { error: false };
  } catch (error) {
    yield put({ type: LOAN_DETAIL_ADD_REFERENCE.failure, payload: error });

    return { error: true };
  }
}

export const LOAN_DETAIL_UPDATE_REFERENCES = generateDefaultActions("LOAN_DETAIL_UPDATE_REFERENCES");
export const loanUpdateReferences = payload => ({ type: LOAN_DETAIL_UPDATE_REFERENCES.value, payload });
function* loanUpdateReferencesSaga(payload) {
  yield requestSaga(LOAN_DETAIL_UPDATE_REFERENCES, "/updatereference", "POST", payload);
}

export const LOAN_DETAIL_DELETE_REFERENCE = generateDefaultActions("LOAN_DETAIL_DELETE_REFERENCE");
export const loanDeleteReference = payload => makePayload(LOAN_DETAIL_DELETE_REFERENCE.value, payload);
function* loanDeleteReferenceSaga(payload) {
  const callback = handleCallback(payload);
  yield call(authorizedRequest, `/reference/${payload.reference.id}`, "DELETE");
  yield put({ type: LOAN_DETAIL_DELETE_REFERENCE.success, originalPayload: payload.reference });
  yield callback();
}
// END REFERENCES

// START ATTACHMENTS
export const loanGetAttachments = payload => makePayload(LOAN_DETAIL_GET_ATTACHMENTS.value, payload);
export const LOAN_DETAIL_GET_ATTACHMENTS = generateDefaultActions("LOAN_DETAIL_GET_ATTACHMENTS");
function* loanGetAttachmentsSaga(payload) {
  const url = "/getFiles/byModel";
  const requestSaga = subKeySaga(payload.subKey);
  yield requestSaga(LOAN_DETAIL_GET_ATTACHMENTS, url, "POST", payload);
}

export const LOAN_DETAIL_PUT_ATTACHMENT = generateDefaultActions("LOAN_DETAIL_PUT_ATTACHMENT");
export const loanPutAttachment = payload => makePayload(LOAN_DETAIL_PUT_ATTACHMENT.value, payload);
function* loanPutAttachmentSaga(payload) {
  const url = "/files";
  payload = {
    ...payload,
    id: payload.file.id,
    isPrivate: payload.file.isPrivate,
    model: "loan",
    modelId: payload.loanId,
    url: "/files",
    name: payload.file.name
  };

  yield requestSaga(LOAN_DETAIL_PUT_ATTACHMENT, url, "PUT", payload);
  yield put(loanGetAttachments({ modelName: "loan", modelId: payload.loanId }));
}

export const LOAN_DETAIL_POST_ATTACHMENT = generateDefaultActions("LOAN_DETAIL_POST_ATTACHMENT");
export const loanPostAttachment = payload => makePayload(LOAN_DETAIL_POST_ATTACHMENT.value, payload);
function* loanPostAttachmentSaga(payload) {
  const callback = handleCallback(payload);
  payload = {
    ...payload,
    model: "loan",
    modelId: payload.loanId,
    url: "/files"
  };

  yield call(uploadFile, payload);
  yield put(LOAN_DETAIL_POST_ATTACHMENT.createSuccess());
  yield put(loanGetAttachments({ modelName: "loan", modelId: payload.loanId }));
  yield callback();
}

export const LOAN_DETAIL_DELETE_ATTACHMENT = generateDefaultActions("LOAN_DETAIL_DELETE_ATTACHMENT");
export const loanDeleteAttachment = payload => makePayload(LOAN_DETAIL_DELETE_ATTACHMENT.value, payload);
function* loanDeleteAttachmentSaga(payload) {
  const url = `/files/${payload.fileId}`;
  const callback = handleCallback(payload);

  yield requestSaga(LOAN_DETAIL_DELETE_ATTACHMENT, url, "DELETE", payload);
  yield put(loanGetAttachments({ modelName: "loan", modelId: payload.loanId }));
  yield callback();
}
// END ATTACHMENTS

export const LOAN_DETAIL_DOWNLOAD_LOAN_DETAILS = generateDefaultActions("LOAN_DETAIL_DOWNLOAD_LOAN_DETAILS");
export const downloadLoan = payload => makePayload(LOAN_DETAIL_DOWNLOAD_LOAN_DETAILS.value, payload);
function* downloadLoanSaga(payload) {
  /*
   * try {
   * yield put(DEAL_DOWNLOAD_LOAN_DETAILS.setIsDownloading({ isDownloading: true }));
   */
  yield call(downloadFile, "downloadLoanSummaries", "POST", payload, 600000);
  /*
   * yield put(GET_DASHBOARD_PDF_DEAL_SUMMARY.createSuccess(payload));
   * } catch (error) {
   *   yield put(GET_DASHBOARD_PDF_DEAL_SUMMARY.createFailure(error));
   * } finally {
   *   yield put(DASHBOARD_IS_DOWNLOADING.setIsDownloading({ isDownloading: false }));
   * }
   */
}

// REDUCER
const initialState = {
  loan: null,
  deal: null,
  lenders: [],
  brokers: [],
  lenderCounsels: []
};

export function loanDetailReducer(state = initialState, action) {
  const { type, payload, originalPayload } = action;
  switch (type) {
    case LOAN_DETAIL_GET_LOAN.success:
      return multipleObjectReturn(state, ["loan", "guestPermission"], [payload.data, payload.guestPermission]);

    case LOAN_DETAIL_GET_DEAL_METRICS.success:
      return defaultReturn(state, payload.data, "deal");

    case LOAN_DETAIL_DELETE_LOAN.success:
      ToastSuccess("Successfully Deleted");
      ToastInfo("Recalculating deal metrics. This may take a couple minutes");

      return defaultReturn(state);

    case LOAN_DETAIL_UPDATE_LOAN_DETAILS.success:
      ToastSuccess("Successfully updated");

      return defaultReturn(state);

    case LOAN_DETAIL_UPDATE_LOAN_DETAILS.failure:
      ToastError("Update failed. Please ensure valid inputs.");

      return defaultReturn(state);

    case LOAN_DETAIL_UPDATE_FIELD_SUMMARY.success:
      ToastSuccess("Successfully updated");

      return defaultReturn(state);

    case LOAN_DETAIL_UPDATE_FIELD_SUMMARY.failure:
      ToastError("Update failed.");

      return defaultReturn(state);

    // ATTACHMENTS
    case LOAN_DETAIL_GET_ATTACHMENTS.success:
      return defaultReturn(state, payload.data, "attachments");

    case LOAN_DETAIL_PUT_ATTACHMENT.success:
      ToastSuccess("File Successfully Updated");

      return defaultReturn(state);

    case LOAN_DETAIL_POST_ATTACHMENT.success:
      ToastSuccess("File Successfully Added");

      return defaultReturn(state);

    case LOAN_DETAIL_DELETE_ATTACHMENT.success:
      ToastSuccess("File Successfully Deleted");

      return defaultReturn(state);

    // NOTES
    case LOAN_DETAIL_GET_NOTES.success:
      return defaultReturn(state, payload.data, "notes");

    case LOAN_DETAIL_POST_NOTE.success:
      ToastSuccess("Note Successfully Added");

      return defaultReturn(state);

    case LOAN_DETAIL_PUT_NOTE.success:
      ToastSuccess("Note Successfully Updated");

      return defaultReturn(state);

    case LOAN_DETAIL_DELETE_NOTE.success:
      ToastSuccess("Note Successfully Deleted");

      return defaultReturn(state);

    // REFERENCES
    case LOAN_DETAIL_GET_REFERENCE_FIELDS.success:
      return defaultReturn(state, payload.data, "referenceFields");

    case LOAN_DETAIL_ADD_REFERENCE.success:
      state.loan.references.push(payload);
      ToastSuccess("Reference successfully saved");

      return defaultReturn(state);

    case LOAN_DETAIL_ADD_REFERENCE.failure:
      ToastError("Add reference failed");

      return defaultReturn(state);

    case LOAN_DETAIL_DELETE_REFERENCE.success:
      _.remove(state.loan.references, { id: originalPayload.id });

      return defaultReturn(state);

    case LOAN_DETAIL_DELETE_REFERENCE.failure:
      ToastError("Remove reference failed");

      return defaultReturn(state);

    case LOAN_DETAIL_UPDATE_REFERENCES.success:
      ToastSuccess("Reference Updated");

      return defaultReturn(state, payload.data, "referenceFields");

    case LOAN_DETAIL_UPDATE_RECOURSE_SCHEDULE.success:
      ToastSuccess("Schedule Updated");

      return defaultReturn(state);

    default:
      return state;
  }
}

// SAGA
export function* loanDetailSaga() {
  yield takeLatest(LOAN_DETAIL_GET_LOAN.value, loanDetailGetLoanSaga);
  yield takeEvery(LOAN_DETAIL_UPDATE_LOAN_DETAILS.value, updateLoanDetailsSaga);
  yield takeLatest(LOAN_DETAIL_GET_DEAL_METRICS.value, loanDetailGetDealMetricsSaga);
  yield takeLatest(LOAN_DETAIL_GET_ATTACHMENTS.value, loanGetAttachmentsSaga);
  yield takeLatest(LOAN_DETAIL_PUT_ATTACHMENT.value, loanPutAttachmentSaga);
  yield takeLatest(LOAN_DETAIL_POST_ATTACHMENT.value, loanPostAttachmentSaga);
  yield takeLatest(LOAN_DETAIL_DELETE_ATTACHMENT.value, loanDeleteAttachmentSaga);
  yield takeLatest(LOAN_DETAIL_GET_NOTES.value, loanGetNotesSaga);
  yield takeLatest(LOAN_DETAIL_PUT_NOTE.value, loanPutNoteSaga);
  yield takeLatest(LOAN_DETAIL_POST_NOTE.value, loanPostNoteSaga);
  yield takeLatest(LOAN_DETAIL_DELETE_NOTE.value, loanDeleteNoteSaga);
  yield takeLatest(LOAN_DETAIL_UPDATE_FIELD_SUMMARY.value, updateFieldSummarySaga);
  yield takeLatest(LOAN_DETAIL_GET_REFERENCE_FIELDS.value, loanGetReferenceFieldsSaga);
  yield takeLatest(LOAN_DETAIL_ADD_REFERENCE.value, loanAddReferenceSaga);
  yield takeLatest(LOAN_DETAIL_DELETE_REFERENCE.value, loanDeleteReferenceSaga);
  yield takeLatest(LOAN_DETAIL_DELETE_LOAN.value, deleteLoanSaga);
  yield takeLatest(LOAN_DETAIL_UPDATE_REFERENCES.value, loanUpdateReferencesSaga);
  yield takeLatest(LOAN_DETAIL_UPDATE_RECOURSE_SCHEDULE.value, saveRecourseScheduleChangeSaga);
  yield takeLatest(LOAN_DETAIL_DOWNLOAD_LOAN_DETAILS.value, downloadLoanSaga);
}

